import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import styles from './PrizeItem.module.css';
import type { ITournamentPrize } from 'features/campaigns';
import { useCampaignCardScale } from 'features/campaigns';
import { getPrizeText } from '../../../../../../utils/campaignHelpers';
import { GET_PRIZE_TEXT_GIFT_SPIN, GET_PRIZE_TEXT_MONEY } from '../../../../../../../../constants';
import Image from '../../../../../../../../shared/components/Image';
import { styled } from 'styled-components';
import TransWithFallback from '../../../../../../../../shared/components/Translations/TransWrapper';

const PrizeImage = styled(Image)<{ place: number }>`
  position: relative;
  top: 16px;
  height: ${({ place }) => (place === 1 ? '105px' : place === 2 || place === 3 ? '94px' : '64px')};

  @media (max-width: 1024px) {
    height: ${({ place }) => (place === 1 ? '87px' : place === 2 || place === 3 ? '69px' : '64px')};
  }

  @media (max-width: 640px) {
    height: ${({ place }) => (place === 1 ? '76px' : place === 2 || place === 3 ? '64px' : '64px')};
  }

  @media (max-width: 1024px), ((min-width: 768px) and (max-width: 1024px) and (orientation: landscape)), (orientation: portrait) {
    top: ${({ place }) => (place === 1 ? '12px' : '11px')};
  }
`;

// Podium ordering for the top-3 row: 1st place sits in the middle, 2nd on the
// left, 3rd on the right. Falls back to 0 for any other place.
const PODIUM_ORDER: Record<number, number> = { 1: 2, 2: 1, 3: 3 };

const PrizeItem = ({ place, type, name, currencyCode, positionRange, giftSpinConfig }: ITournamentPrize) => {
  const { t, i18n } = useTranslation();

  const isTopWinningPlace = typeof place === 'number' && place >= 0 && place < 4;
  const isMinorWinningPlace = place > 3;
  const isGiftSpinPrize = type === GET_PRIZE_TEXT_GIFT_SPIN;

  const prizeText = getPrizeText({ type, name });

  // Scale the prize text down to fit inside the fixed-width .prizeBox (top places)
  // when it would otherwise overflow — reuses the shared campaign text-scaling hook.
  const { titleRef: prizeScaleRef, scale: prizeScale } = useCampaignCardScale(
    `${prizeText}${type === GET_PRIZE_TEXT_MONEY ? currencyCode ?? '' : ''}${isGiftSpinPrize ? name : ''}`,
    { recalculateKey: i18n.language }
  );

  const giftSpinBetText =
    giftSpinConfig?.bet != null
      ? t('campaigns.bet_amount', {
          amount: giftSpinConfig.bet.toFixed(2),
          currency: currencyCode.toUpperCase(),
          defaultValue: '{{amount}} {{currency}} bet',
        })
      : t('tournaments.gift_spin_min_bet', 'Min bet');

  const fistWinnigPlaces = (
    <div className={styles.prizeContainer} style={{ order: PODIUM_ORDER[place] ?? 0 }}>
      <PrizeImage
        place={place}
        src={`/group_tournament/default/prize_${place}`}
        manualAffix=".png"
        alt={`Prize for place ${place}`}
      />
      <div className={clsx(styles.prizeBox, isGiftSpinPrize && styles.prizeBoxWithBet)}>
        <div
          className={styles.prizeText}
          ref={prizeScaleRef as React.RefObject<HTMLDivElement>}
          style={{ transform: prizeScale < 1 ? `scale(${prizeScale})` : undefined }}
        >
          <span className={styles.prizeTextSpan} data-top-wins={String(isTopWinningPlace)} prize-type={type}>
            {prizeText}
          </span>
          {type === GET_PRIZE_TEXT_MONEY && currencyCode && (
            <span className={styles.currencyText} data-top-wins={String(isTopWinningPlace)}>
              {currencyCode.toUpperCase()}
            </span>
          )}
          {isGiftSpinPrize && (
            <span className={styles.giftSpinText} data-top-wins={String(isTopWinningPlace)}>
              <TransWithFallback
                i18nKey={`tournaments.gift_spin${name !== '1' ? 's' : ''}_inline`}
                customFallbackTranslation={name !== '1' ? 'Gift Spins' : 'Gift Spin'}
              />
            </span>
          )}
        </div>
        {isGiftSpinPrize && (
          <div className={styles.giftSpinBetText} data-top-wins={String(isTopWinningPlace)}>
            {giftSpinBetText}
          </div>
        )}
      </div>
    </div>
  );

  const minorWinningPlaces = (
    <div className={clsx(styles.minorPrizeContainer, isGiftSpinPrize && styles.minorPrizeContainerWithBet)}>
      <div className={styles.minorPrizePositionText}>
        {t('campaigns.position', 'Position')} {positionRange ? positionRange : place}
      </div>
      <div className={clsx(styles.minorPrizeValue, isGiftSpinPrize && styles.minorPrizeValueWithBet)}>
        <div>
          <span className={styles.prizeTextSpan} data-top-wins={String(isTopWinningPlace)} prize-type={type}>
            {prizeText}
          </span>
          {type === GET_PRIZE_TEXT_MONEY && currencyCode && (
            <span className={styles.currencyText} data-top-wins={String(isTopWinningPlace)}>
              {currencyCode.toUpperCase()}
            </span>
          )}
          {isGiftSpinPrize && (
            <span className={styles.giftSpinText} data-top-wins={String(isTopWinningPlace)}>
              <TransWithFallback
                i18nKey={`tournaments.gift_spin${name !== '1' ? 's' : ''}_inline`}
                customFallbackTranslation={name !== '1' ? 'Gift Spins' : 'Gift Spin'}
              />
            </span>
          )}
        </div>
        {isGiftSpinPrize && (
          <div className={styles.giftSpinBetText} data-top-wins={String(isTopWinningPlace)}>
            {giftSpinBetText}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {isTopWinningPlace && fistWinnigPlaces}
      {isMinorWinningPlace && minorWinningPlaces}
    </>
  );
};

export default memo(PrizeItem);
